import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, X } from 'lucide-react';
import { ReactNode } from 'react';
import Button from './Button';

interface SuccessModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onDismiss?: () => void;
  title: string;
  subtitle?: string;
  message?: string;
  body?: string;
  middleContent?: ReactNode;
  noteBox?: string;
}

export default function SuccessModal({
  isOpen,
  onClose,
  onDismiss,
  title,
  subtitle,
  message,
  body,
  middleContent,
  noteBox,
}: SuccessModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const dismiss = onDismiss || onClose;
        if (dismiss) dismiss();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose, onDismiss]);

  if (!isOpen) return null;

  const handleDismiss = onDismiss || onClose || (() => {});

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center animate-in fade-in duration-200"
      style={{
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(4px)',
        padding: 'max(16px, env(safe-area-inset-top)) 16px max(16px, env(safe-area-inset-bottom)) 16px',
      }}
    >
      <div
        className="relative bg-[#141414] border rounded-2xl w-full animate-in zoom-in-95 duration-300"
        style={{
          maxWidth: 'min(92vw, 448px)',
          maxHeight: 'min(90vh, calc(100vh - 32px))',
          overflow: 'auto',
          borderColor: 'rgba(64, 224, 208, 0.55)',
          boxShadow: '0px 12px 28px 3px rgba(0, 0, 0, 0.52), 0px 2px 8px 1px rgba(0, 0, 0, 0.16)',
        }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-[#808080] hover:text-[#A0A0A0]" />
        </button>

        <div className="px-6 pt-8 pb-4 text-center">
          <div className="mb-4 flex justify-center">
            <div className="w-16 h-16 bg-[#40E0D0]/20 rounded-full flex items-center justify-center">
              <Check className="w-8 h-8 text-[#40E0D0]" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-3">
            {title}
          </h2>

          {subtitle && (
            <p className="text-white/60 text-sm">
              {subtitle}
            </p>
          )}
        </div>

        <div className="px-6 pb-8">
          {middleContent && (
            <div className="mt-5 mb-5">
              {middleContent}
            </div>
          )}

          {(message || body) && (
            <p className="text-white/80 text-sm mb-6">
              {message || body}
            </p>
          )}

          {noteBox && (
            <div className="w-full bg-white/5 border border-white/10 rounded-lg p-4 mb-6">
              <p className="text-xs text-white/60 text-center leading-relaxed">
                {noteBox}
              </p>
            </div>
          )}

          <Button
            onClick={handleDismiss}
            variant="primary"
            fullWidth
          >
            Continue Exploring
          </Button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
